import { z, ZodTypeAny } from 'zod';

import {
  CourseCompare,
  DisabilityType,
  EmploymentType,
  EnglishTestNotReqdReason,
  EnglishTestType,
  Ethnicity,
  FeeStatus,
  FirstApplicationStatus,
  Gender,
  GradeLevel,
  GradeSystem,
  IDType,
  PreviousStudyLevel,
  ReferralSource,
  RefusedVisaType,
  ResultStatus,
  StudyLevel,
  // EnglishTestNotReqdReason,
  UkAccommodationType,
  VisaLevelOfStudy,
  VisaOrPermitType,
  VisaType
} from '../enums';
import { RouteStatus, SectionRoute, YourInformationTaskRoute } from '../route';

import { intakeIdValidator } from '../schemas';
import { PassportSchema } from '../shared';

export * from './auth';
export * from './dashboard-row';

export const jsonParser = <T extends ZodTypeAny>(schema: T) =>
  z.preprocess((val, ctx) => {
    try {
      return typeof val === 'string' ? JSON.parse(val) : val;
    } catch (e) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: e instanceof Error ? e.message : 'Invalid JSON format.'
      });
      return z.NEVER;
    }
  }, schema);

export const AttachDocumentSchema = z.object({
  sKey: z.string(),
  s3Key: z.string()
});

export const StudyPlansSchema = z.object({
  intakeId: intakeIdValidator,
  intendedLevelOfStudy: z.preprocess(val => (val === '' ? undefined : val), z.nativeEnum(StudyLevel)),
  courseId: z.string().min(1, { message: 'Required' })
});

export const AddressSchema = z.object({
  addressLine1: z.string().min(1, "Address can't be empty."),
  addressLine2: z.string().optional(),
  addressLine3: z.string().optional(),
  city: z.string().min(1, "City can't be empty."),
  state: z.string().optional(),
  postcode: z.string().min(1, "Postcode can't be empty.").max(20, 'Postcode is too long.'),
  country: z.string()
});

export const FeeStatusSchema = z.object({
  feeStatus: z.nativeEnum(FeeStatus)
});

export const VisaRequiredSchema = z.object({
  isVisaRequired: z.boolean()
});

export const HighestQualificationSchema = z.object({
  highestQualification: z.string()
});

export const ContactInformationSchema = z.object({
  nationality: z.string(),
  dob: z
    .string()
    .date()
    .refine(
      val => {
        const [YYYY, MM, DD] = val.split('-');
        return (
          DD &&
          MM &&
          YYYY &&
          DD.length === 2 &&
          MM.length === 2 &&
          YYYY.length === 4 &&
          parseInt(DD) <= 31 &&
          parseInt(MM) <= 12 &&
          parseInt(YYYY) > 1900
        );
      },
      { message: 'Date of birth is incomplete or invalid.' }
    )
    .refine(
      val => {
        const now = new Date();
        const dob = new Date(val);
        // not equal dateStrings allows to return FALSE if dob and now are the same date
        // allow only dates before now
        return dob < now && dob.toDateString() !== now.toDateString();
      },
      { message: 'Date of birth cannot be in the future or today' }
    )
    .refine(
      val => {
        const [year, month, day] = val.split('-').map(Number);
        const today = new Date();
        const todayMonth = today.getMonth() + 1; // getMonth() is zero based: Jan = 0
        const age =
          today.getFullYear() - year - (todayMonth < month || (todayMonth === month && today.getDate() < day) ? 1 : 0);
        return age > 13;
      },
      { message: 'Must be older than 13 years old.' }
    ),
  phoneNumber: z
    .string()
    .includes('-')
    .min(2)
    .regex(/^\d+(-\d+)*$/, 'Invalid phone number.')
});

export const QualificationSchema = z
  .object({
    level: z.string().refine(val => val in GradeLevel),
    otherQualificationName: z.string().nullish(),
    subject: z.string().min(1, "Subject can't be empty").max(100, 'Subject is too long.'),
    gradeSystem: z.string().refine(val => val in GradeSystem),
    grade: z.string().min(1, "Grade can't be empty").max(100, 'Grade is too long.'),
    graduationDate: z.string().refine(
      val => {
        const currentYear = new Date().getFullYear();
        const MIN_YEAR = currentYear - 130;
        const MAX_YEAR = currentYear + 50;
        const [year] = val.split('-');
        const graduationDateYear = year && year.length === 4 ? new Date(year).getFullYear() : undefined;
        return graduationDateYear && graduationDateYear > MIN_YEAR && graduationDateYear < MAX_YEAR;
      },
      { message: 'Invalid date' }
    ),
    taughtInEnglish: z.boolean().nullish(),
    modules: z.array(
      z.object({
        subject: z.string().min(1, "Subject can't be empty").max(100, 'Subject is too long.'),
        grade: z.string().min(1, "Grade can't be empty").max(100, 'Grade is too long.')
      })
    )
  })
  .superRefine((values, ctx) => {
    const { level, otherQualificationName } = values;
    const isLevelOther = GradeLevel[level as keyof typeof GradeLevel] === GradeLevel.Other;
    if (isLevelOther && typeof otherQualificationName !== 'string') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Qualification name is required',
        path: ['otherQualificationName']
      });
    }
  })
  .superRefine((data, ctx) => {
    const { grade, gradeSystem } = data;

    const gradeValidations: Record<string, { pattern: RegExp; maxLength: number }> = {
      DEGREE_CLASSIFICATION: { pattern: /^[A-Za-z0-9]+$/, maxLength: 100 },
      GPA_4: { pattern: /^[0-9][0-9.,;:/*]*$/, maxLength: 7 },
      GPA_5: { pattern: /^[0-9][0-9.,;:/*]*$/, maxLength: 7 },
      GPA_10: { pattern: /^[0-9][0-9.,;:/*]*$/, maxLength: 7 },
      LETTER_GRADE: { pattern: /^[A-Za-z][A-Za-z.,;:/*]*$/, maxLength: 10 },
      PASS_FAIL: { pattern: /^[A-Za-z]+$/, maxLength: 4 },
      PERCENTAGE: { pattern: /^[0-9.,;:/*%]+$/, maxLength: 8 },
      OTHER: { pattern: /^[A-Za-z0-9]+$/, maxLength: 100 }
    };

    const gradeFormatMessages: Record<string, string> = {
      DEGREE_CLASSIFICATION: 'Grade must contain only alphanumeric characters',
      GPA_4: 'Grade must contain only numbers and the following characters: . , ; : / *',
      GPA_5: 'Grade must contain only numbers and the following characters: . , ; : / *',
      GPA_10: 'Grade must contain only numbers and the following characters: . , ; : / *',
      LETTER_GRADE: 'Grade must contain only letters and the following characters: . , ; : / *',
      PASS_FAIL: 'Grade must contain only letters',
      PERCENTAGE: 'Grade must contain only numbers and the following characters: . , ; : / * %',
      OTHER: 'Grade must contain only alphanumeric characters'
    };

    const validation = gradeValidations[gradeSystem as GradeSystem];
    if (!validation) return; // Skip if gradeSystem is not recognized

    if (grade.length > validation.maxLength) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Grade cannot be longer than ${validation.maxLength} characters for ${gradeSystem} system`,
        path: ['grade']
      });
    }

    if (!validation.pattern.test(grade)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: gradeFormatMessages[gradeSystem as GradeSystem] || 'Invalid grade format',
        path: ['grade']
      });
    }
  });

/**
 *
 * Adds validation to QualificationSchema with `StudySchema.date` and `StudySchema.validUntil`
 *
 * @param date
 * @param validUntil
 * @returns z.ZodEffects<typeof QualificationSchema>
 */
export const enhanceQualificationSchema = (date: string, country: string): z.ZodEffects<typeof QualificationSchema> => {
  return QualificationSchema.superRefine((data, ctx) => {
    // dates validation
    const startDate = new Date(date);
    const targetDate = new Date(data.graduationDate);
    const isDateValid = targetDate >= startDate;
    if (!isDateValid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Qualification date must be after ${date}`,
        path: ['graduationDate']
      });
    }

    //isUk validation
    const isUk = country === 'United Kingdom';
    if (!isUk && typeof data.taughtInEnglish !== 'boolean') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Required`,
        path: ['taughtInEnglish']
      });
    }
  });
};

export const StudySchema = z
  .object({
    level: z.nativeEnum(PreviousStudyLevel),
    resultStatus: z.nativeEnum(ResultStatus),
    country: z.string().min(1, `Country can't be empty`),
    institutionName: z.string().min(1, "The name can't be empty"),
    institutionAddress: jsonParser(
      AddressSchema.omit({ country: true }).extend({
        state: z.string().optional() // Override the state field to be optional
      })
    ),
    date: z.string().refine(
      val => {
        const currentYear = new Date().getFullYear();
        const MIN_YEAR = currentYear - 130;
        const MAX_YEAR = currentYear + 50;
        const [year, date] = val.split('-');
        const isValidDate = date && date.length === 2;
        const validDateYear = year && year.length === 4 ? new Date(year).getFullYear() : undefined;
        return validDateYear && validDateYear > MIN_YEAR && validDateYear < MAX_YEAR && isValidDate;
      },
      { message: 'Invalid date' }
    ),
    validUntil: z.string().refine(
      val => {
        const currentYear = new Date().getFullYear();
        const MIN_YEAR = currentYear - 130;
        const MAX_YEAR = currentYear + 50;
        const [year, date] = val.split('-');
        const isValidDate = date && date.length === 2;
        const validDateYear = year && year.length === 4 ? new Date(year).getFullYear() : undefined;
        return validDateYear && validDateYear > MIN_YEAR && validDateYear < MAX_YEAR && isValidDate;
      },
      { message: 'Invalid date' }
    ),
    academicTranscriptDocument: AttachDocumentSchema.optional(),
    qualifications: z.record(z.string(), QualificationSchema).optional()
  })
  .refine(
    values => {
      const { date: startDate, validUntil } = values;
      const [startDateYear, startDateMonth] = startDate.split('-');
      const [validUntilYear, validUntilMonth] = validUntil.split('-');

      if (startDateYear && startDateMonth && validUntilYear && validUntilMonth) {
        return new Date(startDate) < new Date(validUntil);
      }

      return true;
    },
    { message: 'Start date could not be later then the end date', path: ['date'] }
  );

export const AddUpdateStudyHistorySchema = z.object({
  studyHisitoryId: z.string().optional(),
  action: z.enum(['ADD', 'UPDATE', 'REMOVE']),
  studyHistory: StudySchema.optional()
});

// I think this needs work
export const StudyHistorySchema = z.object({
  latest: StudySchema,
  previous: z.record(z.string(), StudySchema).optional()
});

export const ApplicantReferralSchema = z
  .object({
    firstApplicationStatus: z.nativeEnum(FirstApplicationStatus),
    ucasId: z.string().optional(),
    referralSource: z.nativeEnum(ReferralSource),
    studentReferrerName: z.string().optional(),
    previousApplicationOrStudentNumber: z.string().optional(),
    previousStudentNumber: z.string().optional()
  })
  .refine(
    val => {
      if (val.firstApplicationStatus === FirstApplicationStatus.FirstTime) {
        return val.referralSource !== undefined;
      }
      return true;
    },
    {
      message: 'Referral source is required for first-time applications',
      path: ['referralSource']
    }
  )
  .refine(
    val => {
      if (val.firstApplicationStatus === FirstApplicationStatus.AppliedOnly) {
        return (
          val.previousApplicationOrStudentNumber !== undefined && val.previousApplicationOrStudentNumber.trim() !== ''
        );
      }
      return true;
    },
    {
      message: 'Previous application number is required when you have applied before',
      path: ['previousApplicationOrStudentNumber']
    }
  )
  .refine(
    val => {
      if (val.firstApplicationStatus === FirstApplicationStatus.StudiedBefore) {
        return val.previousStudentNumber !== undefined && val.previousStudentNumber.trim() !== '';
      }
      return true;
    },
    {
      message: 'Previous student number is required when you have enrolled before',
      path: ['previousStudentNumber']
    }
  );

export const OtherNameSchema = z.object({
  givenName: z.string().min(1),
  middleName: z.string().optional(),
  familyName: z.string().min(1),
  reason: z.string().min(1)
});

export const ApplicantIdentitySchema = z.object({
  givenName: z.string().min(1),
  middleName: z.string().optional(),
  familyName: z.string().min(1),
  title: z.string().min(1),
  gender: z.nativeEnum(Gender),

  passport: PassportSchema.nullish(),
  idType: z.nativeEnum(IDType),
  otherNames: z.record(z.string(), OtherNameSchema).nullish(),
  passportDocument: AttachDocumentSchema.optional(),
  idDocument: AttachDocumentSchema.optional()
});

// TODO: remove this schema when start with this ticket ENROLY-1320
export const ApplicantIdentityReactHookFormValidation = ApplicantIdentitySchema.extend({
  hasOtherName: z.boolean(),
  otherNames: z.array(OtherNameSchema).nullish()
}).refine(
  values => {
    const { hasOtherName, otherNames } = values;
    if (hasOtherName) {
      return (otherNames ?? []).length !== 0;
    }
    return true;
  },
  { message: 'Add other name', path: ['otherNames'] }
);

export const OtherNamePayloadSchema = z.object({
  otherName: OtherNameSchema
});

export const OtherNamesSchema = z.object({
  otherNames: z.record(z.string(), OtherNameSchema).nullish()
});

export const DisiblityTypeSchema = z.nativeEnum(DisabilityType);

export const DiversitySchema = z.object({
  ethnicity: z.nativeEnum(Ethnicity),
  hasDisability: z.boolean(),
  disabilityTypes: z.array(DisiblityTypeSchema).nullish().default(null),
  hasBeenInCare: z.boolean().nullish().default(null),
  careStartDate: z
    .string()
    .nullish()
    .default(null)
    .refine(
      value => {
        if (value === null) return true;
        const currentYear = new Date().getFullYear();
        const MIN_YEAR = currentYear - 130;
        const MAX_YEAR = currentYear + 50;
        const [year, date] = (value ?? '').split('-');
        const isValidDate = date && date.length === 2;
        const validDateYear = year && year.length === 4 ? new Date(year).getFullYear() : undefined;
        return validDateYear && validDateYear > MIN_YEAR && validDateYear < MAX_YEAR && isValidDate;
      },
      { message: 'Invalid date' }
    ),
  careEndDate: z
    .string()
    .nullish()
    .default(null)
    .refine(
      value => {
        if (value === null) return true;
        const currentYear = new Date().getFullYear();
        const MIN_YEAR = currentYear - 130;
        const MAX_YEAR = currentYear + 50;
        const [year, date] = (value ?? '').split('-');
        const isValidDate = date && date.length === 2;
        const validDateYear = year && year.length === 4 ? new Date(year).getFullYear() : undefined;
        return validDateYear && validDateYear > MIN_YEAR && validDateYear < MAX_YEAR && isValidDate;
      },
      { message: 'Invalid date' }
    ),
  careDetails: z.string().nullish().default(null),
  occupation: z.string().min(1, 'Cannot be empty').nullish().default(null),
  disabilityTypeOther: z.string().nullish().default(null),
  lastSchoolDescription: z.string().nullish().default(null),
  hasParentsInHE: z.boolean().nullish().default(null),
  isDisabilityStudentAllowance: z.boolean().nullish().default(null)
});

export const getDiversityValidationSchema = (isHomeStudent: boolean = false) => {
  const currentDate = new Date();

  return DiversitySchema.refine(
    values => {
      const { hasBeenInCare, careStartDate, careEndDate } = values;

      if (hasBeenInCare) {
        if (!careStartDate || !careEndDate) {
          return true;
        }

        const startDate = new Date(careStartDate);
        const endDate = new Date(careEndDate);

        return startDate < currentDate && endDate < currentDate && startDate <= endDate;
      }

      return true;
    },
    {
      message:
        'Invalid care dates: start date and end date must be in the past, and start date should not be after end date.',
      path: ['careStartDate']
    }
  ).superRefine((values, ctx) => {
    const {
      ethnicity,
      hasDisability,
      disabilityTypes = [],
      disabilityTypeOther,
      hasBeenInCare,
      careStartDate,
      careEndDate,
      careDetails
    } = values;

    const isEthnicityValid = !!ethnicity && typeof hasDisability === 'boolean';

    if (!isEthnicityValid)
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['ethnicity'], message: 'Please select an ethnicity' });

    const isDisabilityTypeValid = !hasDisability || (hasDisability && disabilityTypes && disabilityTypes.length > 0);
    if (!isDisabilityTypeValid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['disabilityTypes'],
        message: 'Required'
      });
    }

    const isHealthConditionValid =
      !hasDisability ||
      (disabilityTypes &&
        disabilityTypes.length &&
        (!disabilityTypes.includes(DisabilityType.NotListed) || !!disabilityTypeOther));
    if (!isHealthConditionValid)
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['disabilityTypeOther'],
        message: 'Required'
      });

    const isInCareValid =
      typeof hasBeenInCare !== 'boolean' || !hasBeenInCare || (!!careStartDate && !!careEndDate && !!careDetails);
    if (!isInCareValid) {
      for (const field of ['careStartDate', 'careEndDate', 'careDetails']) {
        if (values[field as keyof typeof values]) continue;
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [field],
          message: 'Required'
        });
      }
    }

    const homeStudentFields = [
      'isDisabilityStudentAllowance',
      'hasBeenInCare',
      'lastSchoolDescription',
      'hasParentsInHE',
      'occupation'
    ];

    const everyHomeFieldNotEmpty = homeStudentFields.every(f => {
      return values[f as keyof typeof values] !== null && values[f as keyof typeof values] !== undefined;
    });

    const isHomeStudentValid = !isHomeStudent || everyHomeFieldNotEmpty;

    if (!isHomeStudentValid) {
      for (const field of homeStudentFields) {
        if (values[field as keyof typeof values] !== null && values[field as keyof typeof values] !== undefined)
          continue;
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [field],
          message: 'Required'
        });
      }
    }

    if (
      !isEthnicityValid ||
      !isDisabilityTypeValid ||
      !isHealthConditionValid ||
      !isInCareValid ||
      !isHomeStudentValid
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'The form is not completed, please fill in all required fields'
      });
    }
  });
};

export const OtherNationalitySchema = z.object({
  isCurrent: z.boolean(),
  nationality: z.string()
});

export const ResidenceEntrySchema = z
  .object({
    countryOfResidence: z.string(),
    description: z.string().nullable(),
    areaOfResidence: z.string().nullable(),
    entryIntoUKDate: z.string().date().nullable()
  })
  // check if description exists when UK is selected
  .refine(
    values => {
      if (values.countryOfResidence === 'United Kingdom') {
        return !!values.description;
      }
      return true;
    },
    {
      message: 'Description is required for UK residence.',
      path: ['description']
    }
  )
  // check if entryIntoUKDate exists when UK is selected
  .refine(
    values => {
      if (values.countryOfResidence === 'United Kingdom') {
        return !!values.entryIntoUKDate;
      }
      return true;
    },
    {
      message: 'Date of entry into the UK is required.',
      path: ['entryIntoUKDate']
    }
  )
  .refine(
    values => {
      if (values.countryOfResidence !== 'United Kingdom') {
        return true;
      }

      const { entryIntoUKDate } = values;
      const [YYYY, MM, DD] = typeof entryIntoUKDate === 'string' ? entryIntoUKDate.split('-') : '';
      return (
        DD &&
        MM &&
        YYYY &&
        DD.length === 2 &&
        MM.length === 2 &&
        YYYY.length === 4 &&
        parseInt(DD) <= 31 &&
        parseInt(MM) <= 12 &&
        parseInt(YYYY) > 1900
      );
    },
    {
      message: 'Date of entry into the UK is incomplete or invalid.',
      path: ['entryIntoUKDate']
    }
  )
  .refine(
    values => {
      if (values.countryOfResidence !== 'United Kingdom') {
        return true;
      }

      const { entryIntoUKDate } = values;
      const now = new Date();
      const dob = new Date(typeof entryIntoUKDate === 'string' ? entryIntoUKDate : '');
      // not equal dateStrings allows to return FALSE if dob and now are the same date
      // allow only dates before now
      return dob < now && dob.toDateString() !== now.toDateString();
    },
    {
      message: 'Date of entry into the UK cannot be in the future or today',
      path: ['entryIntoUKDate']
    }
  )
  // check if areaOfResidence exists when UK is selected
  .refine(
    values => {
      if (values.countryOfResidence === 'United Kingdom') {
        return !!values.areaOfResidence;
      }
      return true;
    },
    {
      message: 'Area of residence is required for UK residence.',
      path: ['areaOfResidence']
    }
  );

export const OtherResidenceEntrySchema = z
  .object({
    countryOfResidence: z.string(),
    yearOfResidence: z.number()
  })
  .refine(
    values => {
      const currentYear = new Date().getFullYear();
      return values.yearOfResidence <= currentYear;
    },
    {
      message: 'Year of residence cannot be in the future',
      path: ['yearOfResidence']
    }
  )
  .refine(
    values => {
      return values.yearOfResidence >= 1900 && values.yearOfResidence.toString().length === 4;
    },
    {
      message: 'Please enter a valid 4-digit year (1900 or later)',
      path: ['yearOfResidence']
    }
  );

export const NationalitySchema = z
  .object({
    applyingFrom: z.string(),
    countryOfBirth: z.string(),
    nationalityForApplicationPurposes: z.string(),
    isOnlyCountry: z.boolean(),
    isOtherResidence: z.boolean(),
    otherNationalities: z.array(OtherNationalitySchema).nullish(),
    primaryResidence: ResidenceEntrySchema,
    otherResidences: z.array(OtherResidenceEntrySchema).nullish(),
    statementsCorrect: z.boolean()
  })
  .refine(
    values => {
      const { isOnlyCountry, otherNationalities } = values;
      if (isOnlyCountry) return otherNationalities && otherNationalities.length > 0;
      return true;
    },
    { message: 'Other nationalities cannot be left empty', path: ['isOnlyCountry'] }
  )
  .refine(
    values => {
      const { isOtherResidence, otherResidences } = values;
      if (isOtherResidence) return otherResidences && otherResidences.length > 0;
      return true;
    },
    { message: 'Other residences cannot be left empty', path: ['isOtherResidence'] }
  );

export const EmploymentHistorySchema = z
  .object({
    jobTitle: z.string().min(1),
    employerName: z.string().min(1),
    employerAddress: z.string().min(1),
    city: z.string().min(1),
    state: z.string().optional(),
    postalCode: z.string().min(1),
    country: z.string().min(1),
    type: z.nativeEnum(EmploymentType),
    startDate: z.string().refine(
      value => {
        const currentYear = new Date().getFullYear();
        const MIN_YEAR = currentYear - 130;
        const MAX_YEAR = currentYear + 50;
        const [year, date] = value.split('-');
        const isValidDate = date && date.length === 2;
        const validDateYear = year && year.length === 4 ? new Date(year).getFullYear() : undefined;
        return validDateYear && validDateYear > MIN_YEAR && validDateYear < MAX_YEAR && isValidDate;
      },
      { message: 'Invalid date' }
    ),
    endDate: z
      .string()
      .refine(
        value => {
          if (value === undefined || value === '') return true;
          const [year] = value.split('-');
          if (year === undefined || year === '') return true;
          const currentYear = new Date().getFullYear();
          const MIN_YEAR = currentYear - 130;
          const MAX_YEAR = currentYear + 50;

          const validDateYear = year && year.length === 4 ? new Date(year).getFullYear() : undefined;
          return validDateYear && validDateYear > MIN_YEAR && validDateYear < MAX_YEAR;
        },
        { message: 'Invalid year' }
      )
      .optional()
  })
  .refine(
    values => {
      const { startDate, endDate } = values;
      if (!endDate) return true;
      return new Date(startDate) < new Date(endDate);
    },
    { message: 'End Date could not be earlier than the Start Date', path: ['endDate'] }
  );

export const ApplicantEmploymentSchema = z.object({
  hasEmploymentHistory: z.boolean(),
  hasCV: z.boolean().optional(),
  employmentHistory: z.array(EmploymentHistorySchema).nullish(),
  cvDocument: AttachDocumentSchema.optional()
});

export const EnglishTestSchema = z
  .object({
    type: z.nativeEnum(EnglishTestType),
    typeOther: z.string().optional(),
    referenceNo: z.string(),
    score: z.string(),
    date: z
      .string()
      .min(1, 'English test date is required')
      .refine(
        value => {
          const currentYear = new Date().getFullYear();
          const MIN_YEAR = currentYear - 130;
          const MAX_YEAR = currentYear;
          const [year, date] = value.split('-');
          const isValidDate = date && date.length === 2;
          const validDateYear = year && year.length === 4 ? new Date(year).getFullYear() : undefined;
          return validDateYear && validDateYear > MIN_YEAR && validDateYear <= MAX_YEAR && isValidDate;
        },
        { message: 'Invalid date' }
      ),
    validUntil: z
      .string()
      .optional()
      .refine(
        value => {
          if (!value) return true; // Optional field
          const currentYear = new Date().getFullYear();
          const MIN_YEAR = currentYear - 130;
          const MAX_YEAR = currentYear + 50;
          const [year, date] = value.split('-');
          const isValidDate = date && date.length === 2;
          const validDateYear = year && year.length === 4 ? new Date(year).getFullYear() : undefined;
          return validDateYear && validDateYear > MIN_YEAR && validDateYear <= MAX_YEAR && isValidDate;
        },
        { message: 'Invalid date' }
      )
  })
  .superRefine((data, ctx) => {
    const { type, typeOther, score, referenceNo, date, validUntil } = data;

    // score validation
    if (!score || score.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['score'],
        message: 'Test score is required'
      });
    }

    // Reference number validation
    if (!referenceNo || referenceNo.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['referenceNo'],
        message: 'Reference number is required'
      });
    }

    // OTHER type test name validation
    if (type === EnglishTestType.Other && (!typeOther || typeOther.trim() === '')) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['typeOther'],
        message: 'Please specify the name of the English test'
      });
    }

    // Test date cannot be in the future
    if (date) {
      const [year, month] = date.split('-');
      if (year && month) {
        const testDate = new Date(date);
        const today = new Date();

        if (testDate > today) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['date'],
            message: 'The English Test Date cannot be later than today'
          });
        }
      }
    }

    // Valid until must be after test date
    if (date && validUntil) {
      const [testYear, testMonth] = date.split('-');
      const [validYear, validMonth] = validUntil.split('-');

      if (testYear && testMonth && validYear && validMonth) {
        // Create dates with first day of month for consistent comparison
        const testDate = new Date(`${testYear}-${testMonth}-01`);
        const validUntilDate = new Date(`${validYear}-${validMonth}-01`);

        if (testDate > validUntilDate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['validUntil'],
            message: 'The Valid Until date cannot be earlier than the Test Date.'
          });
        }
      }
    }
  });

export const EnglishSchema = z
  .object({
    hasEnglishTest: z.boolean(),
    englishTest: EnglishTestSchema.optional().nullish(),
    reasonNotReqd: z.nativeEnum(EnglishTestNotReqdReason).optional().nullish(),
    reasonNotReqdOther: z.string().optional().nullish(),
    englishQualification: z.string().optional().nullish(),
    englishTestDocument: AttachDocumentSchema.optional()
  })
  .superRefine((data, ctx) => {
    // If user has taken an English test
    if (data.hasEnglishTest) {
      if (!data.englishTest) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'English test details are required',
          path: ['englishTest']
        });
      }
    }
    // If user has not taken an English test
    else {
      if (!data.reasonNotReqd) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please select a reason why an English test is not required',
          path: ['reasonNotReqd']
        });
      }

      // If reason is "previous qualification" but no qualification selected
      if (data.reasonNotReqd === EnglishTestNotReqdReason.PrevQual && !data.englishQualification) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please select a qualification',
          path: ['englishQualification']
        });
      }

      // If reason is "other" but no explanation provided
      if (
        data.reasonNotReqd === EnglishTestNotReqdReason.Other &&
        (!data.reasonNotReqdOther || data.reasonNotReqdOther.trim() === '')
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please provide an explanation',
          path: ['reasonNotReqdOther']
        });
      }
    }
  });

export const ContactDetailsSchema = z
  .object({
    residentialAddress: jsonParser(AddressSchema).nullish(),
    residentialPhoneNo: z
      .string()
      .includes('-')
      .min(2, 'Invalid phone number length.')
      .regex(/^\d+(-\d+)*$/, 'Invalid phone number.'),
    contactAddressSameAsResidential: z.boolean(),
    contactAddress: jsonParser(AddressSchema.omit({ country: true }).nullish()),
    termAddressSameAsPermanent: z.boolean().nullish().optional(),
    termAddress: jsonParser(AddressSchema.omit({ country: true }).nullish()),
    hasUkAccommodation: z.boolean().nullish().optional(),
    ukAccommodationType: z
      .string()
      .refine(val => val in UkAccommodationType)
      .nullish(),
    ukAccommodationAddress: jsonParser(AddressSchema.omit({ country: true }).nullish()),
    ukAccommodationPhone: z
      .string()
      .includes('-')
      .min(2)
      .regex(/^\d+(-\d+)*$/, 'Invalid phone number.')
      .nullish()
  })
  .superRefine((data, ctx) => {
    if (data.contactAddressSameAsResidential === false && !data.contactAddress) {
      ctx.addIssue({
        code: 'custom',
        path: ['contactAddress'],
        message: 'Contact address is required when contact address is different'
      });
    }

    if (data.termAddressSameAsPermanent === false && !data.termAddress) {
      ctx.addIssue({
        code: 'custom',
        path: ['termAddress'],
        message: 'Term address is required when term address is different then permanent address'
      });
    }

    if (data.termAddressSameAsPermanent === false && !data.ukAccommodationType) {
      ctx.addIssue({
        code: 'custom',
        path: ['ukAccommodationType'],
        message: 'Accomodation type is required'
      });
    }

    if (data.hasUkAccommodation) {
      if (!data.ukAccommodationType) {
        ctx.addIssue({
          code: 'custom',
          path: ['ukAccommodationType'],
          message: 'Accomodation type is required'
        });
      }
      if (!data.ukAccommodationAddress) {
        ctx.addIssue({
          code: 'custom',
          path: ['ukAccommodationAddress'],
          message: 'Accomodation address is required'
        });
      }
      if (!data.ukAccommodationPhone) {
        ctx.addIssue({
          code: 'custom',
          path: ['ukAccommodationPhone'],
          message: 'Phone number is required'
        });
      }
    }
  });

export const enhanceContactDetailsSchema = (schema: typeof ContactDetailsSchema, feeStatus?: FeeStatus) => {
  return schema.superRefine((data, ctx) => {
    if (feeStatus === FeeStatus.Home) {
      if (data.termAddressSameAsPermanent === undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['termAddressSameAsPermanent'],
          message: 'Required'
        });
      }
    } else if (feeStatus && [FeeStatus.NotSure, FeeStatus.Overseas].includes(feeStatus)) {
      if (data.hasUkAccommodation === undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['hasUkAccommodation'],
          message: 'Required'
        });
      }
    }
  });
};

export const VisaNotListedOption = z.object({
  visaNotListedCustomValue: z.string().nullish()
});

export const UkStudentVisaSchema = z
  .object({
    studentVisaType: z.string().refine(val => val in VisaType),
    levelOfStudy: z.string().refine(val => val in VisaLevelOfStudy),
    visaReceivedYear: z.number().gt(1900).lte(new Date().getFullYear()),
    isCourseCompleted: z.boolean(),
    compareCourse: z.string().refine(val => val in CourseCompare)
  })
  .merge(VisaNotListedOption);

export const UkNonStudentVisasSchema = z
  .object({
    studentVisaType: z.string().refine(val => val in VisaOrPermitType),
    issuedDate: z
      .string()
      .date('Date format is DD MM YYYY')
      .refine(
        val => {
          const [YYYY, MM, DD] = val.split('-');
          return (
            DD &&
            MM &&
            YYYY &&
            DD.length === 2 &&
            MM.length === 2 &&
            YYYY.length === 4 &&
            parseInt(DD) <= 31 &&
            parseInt(MM) <= 12 &&
            parseInt(YYYY) > 1900
          );
        },
        { message: 'Issue date is incomplete or invalid.' }
      )
      .refine(
        val => {
          const now = new Date();
          const issueDate = new Date(val);
          return issueDate < now && issueDate.toDateString() !== now.toDateString();
        },
        { message: 'Issue date cannot be in the future or today' }
      ),
    validTill: z
      .string()
      .optional()
      .refine(
        val => {
          if (!val || val.trim() === '') return true; // Skip validation if empty
          const [YYYY, MM, DD] = val.split('-');
          return (
            DD &&
            MM &&
            YYYY &&
            DD.length === 2 &&
            MM.length === 2 &&
            YYYY.length === 4 &&
            parseInt(DD) <= 31 &&
            parseInt(MM) <= 12 &&
            parseInt(YYYY) > 1900
          );
        },
        { message: 'Valid until date is incomplete or invalid.' }
      )
      .refine(
        val => {
          if (!val || val.trim() === '') return true; // Skip validation if empty
          const date = new Date(val);
          // Check if the date is valid by ensuring it's not NaN
          // and the formatted parts match what was input
          const [inputYear, inputMonth, inputDay] = val.split('-').map(Number);
          return (
            !isNaN(date.getTime()) &&
            date.getFullYear() === inputYear &&
            date.getMonth() + 1 === inputMonth &&
            date.getDate() === inputDay
          );
        },
        { message: 'Please enter a valid date.' }
      )
  })
  .merge(VisaNotListedOption)
  .superRefine((data, ctx) => {
    const issuedDate = new Date(data.issuedDate);
    const validTill = data.validTill ? new Date(data.validTill) : null;

    if (validTill && issuedDate > validTill) {
      ctx.addIssue({
        code: 'custom',
        path: ['issuedDate'],
        message: 'Issued date should be less than valid till date'
      });
    }

    if (VisaOrPermitType[data.studentVisaType as keyof typeof VisaOrPermitType] === VisaOrPermitType.VisaNotListed) {
      if (!data.visaNotListedCustomValue || data.visaNotListedCustomValue.trim().length < 1) {
        ctx.addIssue({
          code: 'custom',
          path: ['visaNotListedCustomValue'],
          message: `Custom visa or permit type is required when student visa type is "${VisaOrPermitType.VisaNotListed}"`
        });
      }
    }
  });

export const UkVisaRefusalSchema = z.object({
  refusedVisaType: z.string().refine(data => data in RefusedVisaType),
  visaRefusalYear: z.number({ invalid_type_error: 'Must be a number' }).min(1900).max(new Date().getFullYear())
});

export const NonUkVisaRefusalSchema = z.object({
  visaRefusalCountry: z.string(),
  isStudentVisaRefused: z.boolean(),
  visaRefusalYear: z.number({ invalid_type_error: 'Must be a number' }).min(1900).max(new Date().getFullYear())
});

function isBritishOrIrish(nationalityForApplicationPurposes: INationality['nationalityForApplicationPurposes']) {
  return nationalityForApplicationPurposes === 'British' || nationalityForApplicationPurposes === 'Irish';
}

function isEmptyOrNonExistentArray(arr: unknown): boolean {
  return !arr || !Array.isArray(arr) || arr.length === 0;
}

export const VisaHistorySchema = z
  .object({
    isVisaRequired: z.boolean().nullish(), //Overseas
    visaExemptionReason: z.string().nullish(), //Overseas
    hasVisaForCurrentOrPreviousUkStudy: z.boolean().nullish(), //Overseas
    hasStudentVisaForThisUkStudy: z.boolean().nullish(), //Overseas
    ukStudentVisas: z.array(UkStudentVisaSchema).nullish(), //Overseas
    hasEuEeaSwitzerlandExperience: z.boolean(), //ALL (HOME AND Overseas and British or Irish)
    hasEuFamilyMember: z.boolean(), //ALL (HOME AND Overseas and British or Irish)
    hasNonStudentVisaOrPermit: z.boolean().nullish(), // HOME (non British or Irish) AND Overseas
    ukNonStudentVisaOrPermit: z.array(UkNonStudentVisasSchema).nullish(), // HOME (non British or Irish) AND Overseas
    hasPreviousVisaRefusal: z.boolean().nullish(), //Overseas
    ukVisaRefusals: z.array(UkVisaRefusalSchema).nullish(), //Overseas
    hasPreviousNonUkVisaRefusal: z.boolean().nullish(), //Overseas
    nonUkVisaRefusals: z.array(NonUkVisaRefusalSchema).nullish() //Overseas
  })
  .superRefine((data, ctx) => {
    const validationRules = [
      {
        condition: data.isVisaRequired === false && !data.visaExemptionReason,
        path: 'visaExemptionReason',
        message: 'Specify visa not required reason'
      },
      {
        condition: data.hasStudentVisaForThisUkStudy && isEmptyOrNonExistentArray(data.ukStudentVisas),
        path: 'ukStudentVisas',
        message: 'Specify your current or previous UK study visas'
      },
      {
        condition: data.hasNonStudentVisaOrPermit && isEmptyOrNonExistentArray(data.ukNonStudentVisaOrPermit),
        path: 'ukNonStudentVisaOrPermit',
        message: 'Specify your UK permit or non-student visas'
      },
      {
        condition: data.hasPreviousVisaRefusal && isEmptyOrNonExistentArray(data.ukVisaRefusals),
        path: 'ukVisaRefusals',
        message: 'Specify if visa is required'
      },
      {
        condition: data.hasPreviousNonUkVisaRefusal && isEmptyOrNonExistentArray(data.nonUkVisaRefusals),
        path: 'nonUkVisaRefusals',
        message: 'Specify your non-UK visa refusals'
      }
    ];

    // Apply all validation rules
    validationRules.forEach(rule => {
      if (rule.condition) {
        ctx.addIssue({
          code: 'custom',
          path: [rule.path],
          message: rule.message
        });
      }
    });
  });

export function enhanceVisaHistorySchema(
  feeStatus: FeeStatus,
  nationalityForApplicationPurposes: INationality['nationalityForApplicationPurposes']
) {
  return VisaHistorySchema.superRefine((data, ctx) => {
    if (!isBritishOrIrish(nationalityForApplicationPurposes)) {
      if (typeof data.hasNonStudentVisaOrPermit !== 'boolean') {
        ctx.addIssue({
          code: 'custom',
          path: ['hasNonStudentVisaOrPermit'],
          message: 'Specify your UK permit or non-student visas'
        });
      }
    }

    if (feeStatus === FeeStatus.Overseas) {
      const requiredFields = [
        'isVisaRequired',
        'hasVisaForCurrentOrPreviousUkStudy',
        'hasPreviousVisaRefusal',
        'hasPreviousNonUkVisaRefusal'
      ];
      requiredFields.forEach(field => {
        const fieldValue = data[field as keyof typeof data];
        if (fieldValue === null || fieldValue === undefined) {
          ctx.addIssue({
            code: 'custom',
            path: [field],
            message: 'This field is required'
          });
        }
      });
    }

    if (data.hasVisaForCurrentOrPreviousUkStudy && typeof data.hasStudentVisaForThisUkStudy !== 'boolean') {
      ctx.addIssue({
        code: 'custom',
        path: ['hasStudentVisaForThisUkStudy'],
        message: 'This field is required'
      });
    }
  });
}

export type HighestQualificationType = {
  code: string;
  codingLabel: string;
};

export interface ApplicantDetails
  extends IContactInformation,
    IFeeStatus,
    IStudyPlans,
    IApplicantReferral,
    IApplicantIdentity,
    INationality,
    IDiversity,
    IApplicantEmployment,
    IStudyPlans,
    // IEnglish,
    Partial<IEnglish>, // Is optional, home applicants for example won't need it
    IContactDetails,
    IVisaHistory {
  userId: string;
  email: string;
  knownBy?: string;
  regionId: string; // Will we need this here or on the application?
  routeStatus: Partial<Record<YourInformationTaskRoute, RouteStatus>> & Partial<Record<SectionRoute, RouteStatus>>;
  studentNo?: string;
  potentialDuplicate?: boolean;
  potentialDuplicateConfirmed?: boolean;
  duplicateMatches?: Array<any>; // This will be properly typed when we have the schema
  agentId?: string;
  highestQualification?: HighestQualificationType;
}

export interface ILead {
  uni: string;
  userId: string;
  email: string;
  agentConsent?: boolean;
  welcomeStatus: boolean;
  referrer: string;
  headers: { [key: string]: string };
  studentAuthorisedAgent?: boolean;
}

export const StudentDuplicateSchema = z
  .object({
    studentNo: z.string().optional(),
    isDuplicate: z.boolean()
  })
  .superRefine((data, ctx) => {
    const { studentNo, isDuplicate } = data;
    if (isDuplicate && !studentNo) {
      ctx.addIssue({
        code: 'custom',
        path: ['studentNo'],
        message: 'Student number is required when isDuplicate is true'
      });
    }
  });

/* eslint-disable @typescript-eslint/no-empty-object-type */
export interface IAttachDocument extends z.infer<typeof AttachDocumentSchema> {}
export interface IFeeStatus extends z.infer<typeof FeeStatusSchema> {}
export interface IVisaRequired extends z.infer<typeof VisaRequiredSchema> {}
export interface IContactInformation extends z.infer<typeof ContactInformationSchema> {}
export interface IStudyPlans extends z.infer<typeof StudyPlansSchema> {}
export interface IQualification extends z.infer<typeof QualificationSchema> {}
export interface IStudy extends z.infer<typeof StudySchema> {}
export interface IStudyHistory extends z.infer<typeof StudyHistorySchema> {}
export interface IApplicantReferral extends z.infer<typeof ApplicantReferralSchema> {}
export interface IDiversity extends z.infer<typeof DiversitySchema> {}

export interface IApplicantIdentity extends z.infer<typeof ApplicantIdentitySchema> {}
export interface IApplicantIdentityReactHookFormValidation
  extends z.infer<typeof ApplicantIdentityReactHookFormValidation> {}

export interface IOtherNames extends z.infer<typeof OtherNamesSchema> {}
export interface IOtherNamePayload extends z.infer<typeof OtherNamePayloadSchema> {}

export interface IOtherName extends z.infer<typeof OtherNameSchema> {}
export interface INationality extends z.infer<typeof NationalitySchema> {}
export interface IEmploymentHistory extends z.infer<typeof EmploymentHistorySchema> {}
export interface IApplicantEmployment extends z.infer<typeof ApplicantEmploymentSchema> {}
export interface IEnglish extends z.infer<typeof EnglishSchema> {}
export interface IEnglishTest extends z.infer<typeof EnglishTestSchema> {}
export interface IContactDetails extends z.infer<typeof ContactDetailsSchema> {}
export interface IVisaHistory extends z.infer<typeof VisaHistorySchema> {}

export interface INonUkVisaRefusalSchema extends z.infer<typeof NonUkVisaRefusalSchema> {}
export interface IUkNonStudentVisaSchema extends z.infer<typeof UkNonStudentVisasSchema> {}
export interface IUkStudentVisaSchema extends z.infer<typeof UkStudentVisaSchema> {}
export interface IUkVisaRefusalSchema extends z.infer<typeof UkVisaRefusalSchema> {}

export interface IAddressSchema extends z.infer<typeof AddressSchema> {}

export interface IVisaNotListedOption extends z.infer<typeof VisaNotListedOption> {}

export interface IStudentDuplicate extends z.infer<typeof StudentDuplicateSchema> {}
